import { SshIconUrl } from "@p0security/shared/integrations/constants";
import { AwsIntegration } from "@p0security/shared/integrations/resources/aws/types";
import { AzureIntegration } from "@p0security/shared/integrations/resources/azure/types";
import { CsaIntegration } from "@p0security/shared/integrations/resources/csa/types";
import { GcloudIntegration } from "@p0security/shared/integrations/resources/gcloud/types";
import { SelfHostedIntegration } from "@p0security/shared/integrations/resources/self-hosted/types";
import { SshComponents } from "@p0security/shared/integrations/resources/ssh/components";
import { TailscaleIntegration } from "@p0security/shared/integrations/resources/tailscale/types";
import { ZpaIntegration } from "@p0security/shared/integrations/resources/zpa/types";
import { Typography } from "antd";
import { useFirestoreDoc } from "providers/FirestoreProvider";
import { useCallback } from "react";

import { Install, InstallProps } from "../Install/Install";
import { sshInstaller } from "./install/install";

const SshPreamble = (
  <>
    <Typography.Paragraph>
      Enable secure SSH access to instances across AWS, Azure, and GCP with
      just-in-time provisioning.
    </Typography.Paragraph>
    <Typography.Text strong>Prerequisites:</Typography.Text>
    <ul className="preamble-list">
      <li>
        P0 IAM integration installed for your cloud provider (AWS, Azure, or
        GCP)
      </li>
    </ul>
  </>
);

export const API_PATH = "integrations/ssh/config";

export const Ssh = (
  props: Pick<InstallProps<typeof SshComponents>, "cardOverride" | "onInstall">
) => {
  const itemKind = useCallback(() => "account", []);

  const aws = useFirestoreDoc<AwsIntegration>("integrations/aws", {
    tenantAware: true,
  });
  const gcloud = useFirestoreDoc<GcloudIntegration>("integrations/gcloud", {
    tenantAware: true,
  });
  const azure = useFirestoreDoc<AzureIntegration>("integrations/azure", {
    tenantAware: true,
  });
  const selfHosted = useFirestoreDoc<SelfHostedIntegration>(
    "integrations/self-hosted",
    {
      tenantAware: true,
    }
  );

  const cspConfigs = { aws, gcloud, azure, "self-hosted": selfHosted };

  const tailscaleConfig = useFirestoreDoc<TailscaleIntegration>(
    "integrations/tailscale",
    {
      tenantAware: true,
    }
  );

  const zpaConfig = useFirestoreDoc<ZpaIntegration>(
    "integrations/zscaler-private-access",
    {
      tenantAware: true,
    }
  );

  const csaConfig = useFirestoreDoc<CsaIntegration>(
    "integrations/cisco-secure-access",
    {
      tenantAware: true,
    }
  );

  const networkAccessConfigs = {
    tailscale: tailscaleConfig,
    "zscaler-private-access": zpaConfig,
    "cisco-secure-access": csaConfig,
  };

  return (
    <Install<typeof SshComponents>
      components={SshComponents}
      installer={sshInstaller(cspConfigs, networkAccessConfigs)}
      integration="ssh"
      itemKind={itemKind}
      logo={SshIconUrl}
      preamble={SshPreamble}
      title={"SSH"}
      {...props}
    />
  );
};
